/**
 * IELTS 考试特定的题目类型定义
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';
import { validateIeltsReadingQuestionFormat } from '../../questionFormatValidators';

/**
 * IELTS 听力题定义
 */
export const ieltsListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'ielts_listening',
    QuestionType.SINGLE_CHOICE,
    'IELTS 听力题',
    {
      requiredStimulus: ['audio'],
      extraFields: {
        // IELTS 听力分为 4 个 Part
        part: createFieldDefinition('part', 'number', true, 'Part 编号 (1-4)', {
          validator: (value) => {
            const num = Number(value);
            if (num < 1 || num > 4) {
              return 'IELTS 听力必须属于 Part 1-4';
            }
            return true;
          },
        }),
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'IELTS 听力题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'form_completion', // 表格填空
                'note_completion', // 笔记填空
                'table_completion', // 表格填写
                'map_labelling', // 地图标注
                'multiple_choice', // 单项选择
                'matching', // 配对
                'sentence_completion', // 句子填空
                'summary_completion', // 摘要填空
                'short_answer', // 简答
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 IELTS 听力题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
        // 音频时长（秒）
        audioDuration: createFieldDefinition(
          'audioDuration',
          'number',
          false,
          '音频时长（秒）'
        ),
      },

      constraints: {
        maxAudioDuration: 600, // 单个 Part 约 7-8 分钟，上限 10 分钟
        supportedFormats: ['mp3', 'wav'],
        requiresTranscript: true, // IELTS 练习提供原文
      },

      customValidator: (question) => {
        const errors = [];

        // IELTS 听力音频时长验证
        if (question.audioDuration) {
          if (question.audioDuration > 600) {
            errors.push({
              field: 'audioDuration',
              message: 'IELTS 听力单个 Part 音频不能超过 10 分钟',
              code: 'IELTS_AUDIO_TOO_LONG',
            });
          }
          if (question.audioDuration < 30) {
            errors.push({
              field: 'audioDuration',
              message: 'IELTS 听力音频不能短于 30 秒',
              code: 'IELTS_AUDIO_TOO_SHORT',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * IELTS 阅读题定义
 */
export const ieltsReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'ielts_reading',
    QuestionType.SINGLE_CHOICE,
    'IELTS 阅读理解题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        // 文章编号（3 篇文章）
        passageNumber: createFieldDefinition(
          'passageNumber',
          'number',
          true,
          '文章编号 (1-3)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num < 1 || num > 3) {
                return 'IELTS 阅读必须属于第 1-3 篇文章';
              }
              return true;
            },
          }
        ),
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'IELTS 阅读题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'multiple_choice', // 单项选择
                'true_false_not_given', // 判断 (True/False/Not Given)
                'yes_no_not_given', // 判断 (Yes/No/Not Given)
                'matching_headings', // 标题配对
                'matching_information', // 信息配对
                'matching_features', // 特征配对
                'sentence_completion', // 句子填空
                'summary_completion', // 摘要填空
                'diagram_labelling', // 图表标注
                'short_answer', // 简答
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 IELTS 阅读题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
        // 材料字数
        wordCount: createFieldDefinition('wordCount', 'number', false, '阅读材料字数'),
      },

      constraints: {
        maxWordCount: 1000, // IELTS 学术类文章约 900 词
      },

      customValidator: (question) => {
        const errors = [];

        // 验证阅读材料长度
        if (question.passage) {
          const wordCount = question.passage.split(/\s+/).length;
          if (wordCount > 1000) {
            errors.push({
              field: 'passage',
              message: 'IELTS 阅读材料不能超过 1000 词',
              code: 'IELTS_PASSAGE_TOO_LONG',
            });
          }
        }

        errors.push(...validateIeltsReadingQuestionFormat(question));

        return errors;
      },
    }
  );

/**
 * IELTS 写作题定义
 */
export const ieltsWritingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'ielts_writing',
    QuestionType.FILL_BLANK, // 写作为开放式作答，用填空类型承载
    'IELTS 写作题',
    {
      responseMode: 'open_ended',
      extraFields: {
        // 任务编号（Task 1 图表/书信，Task 2 议论文）
        task: createFieldDefinition('task', 'number', true, 'Task 编号 (1-2)', {
          validator: (value) => {
            const num = Number(value);
            if (num !== 1 && num !== 2) {
              return 'IELTS 写作必须是 Task 1 或 Task 2';
            }
            return true;
          },
        }),
        // 最低字数要求（Task 1: 150，Task 2: 250）
        minWordCount: createFieldDefinition(
          'minWordCount',
          'number',
          false,
          '最低字数要求'
        ),
      },

      constraints: {
        expectedTaskCount: 2,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证最低字数与 Task 的匹配
        const task = Number(question.task);
        if (!Number.isNaN(task) && question.minWordCount != null) {
          const expectedMin = task === 1 ? 150 : 250;
          if (question.minWordCount < expectedMin) {
            errors.push({
              field: 'minWordCount',
              message: `IELTS 写作 Task ${task} 最低字数要求为 ${expectedMin} 词`,
              code: 'IELTS_WRITING_MIN_WORDS',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * 所有 IELTS 题目类型定义
 */
export const IELTS_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [ieltsListeningDefinition.id]: ieltsListeningDefinition,
  [ieltsReadingDefinition.id]: ieltsReadingDefinition,
  [ieltsWritingDefinition.id]: ieltsWritingDefinition,
};

/**
 * 根据基础类型获取 IELTS 题目类型定义
 */
export function getIELTSQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(IELTS_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 IELTS 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getIELTSSectionSuggestion(
  questionTypeId: string
): 'listening' | 'reading' | 'writing' | null {
  const map: Record<string, 'listening' | 'reading' | 'writing'> = {
    [ieltsListeningDefinition.id]: 'listening',
    [ieltsReadingDefinition.id]: 'reading',
    [ieltsWritingDefinition.id]: 'writing',
  };
  return map[questionTypeId] ?? null;
}
